import z, { type ZodTypeAny } from 'zod';

import type { List } from '@/utils/list';

export interface SingleEquipmentCategoryDto {
  created_at: string;
  id: string;
  name: string;
  updated_at: string;
  equipment_count: number;
}

export type EquipmentCategoriesListDto = List<SingleEquipmentCategoryDto>;

export interface SingleEquipmentCategory {
  createdAt: string;
  id: string;
  name: string;
  updatedAt: string;
  equipment_count: number;
}

export type EquipmentCategoriesList = List<SingleEquipmentCategory>;

export interface SingleEquipmentTypeDto {
  category: SingleEquipmentCategoryDto;
  category_id: string;
  created_at: string;
  id: string;
  name: string;
  updated_at: string;
}

export type EquipmentTypesListDto = List<SingleEquipmentTypeDto>;

export interface SingleEquipmentType {
  category: SingleEquipmentCategory;
  categoryId: string;
  createdAt: string;
  id: string;
  name: string;
  updatedAt: string;
}

export type EquipmentTypesList = List<SingleEquipmentType>;

export interface EquipmentFieldDto {
  item_id: string;
  value: unknown;
}

/** One of the documents attached to an equipment item (vessel `dto.File`). */
export interface EquipmentDocumentDto {
  id: string;
  name: string;
  link: string;
  content_type: string;
  size: number;
}

export interface EquipmentDocument {
  id: string;
  name: string;
  link: string;
  contentType: string;
  size: number;
}

/** Vessel caps an item's attachments at 10 — the 11th is a 400, not a swap. */
export const EQUIPMENT_DOCUMENTS_MAX = 10;

export interface SingleEquipmentDto {
  created_at: string;
  data: {
    [name: string]: string;
  };
  id: string;
  name: string;
  type: SingleEquipmentTypeDto;
  type_id: string;
  updated_at: string;
  vessel_id: string;
  structure_values: null | EquipmentFieldDto[];
  is_ai_generated: boolean;
  /** Up to `EQUIPMENT_DOCUMENTS_MAX` per item — PATCH appends rather than replaces. */
  documents: null | EquipmentDocumentDto[];
  /** One free-text blob per item, not a list of authored notes — '' when unset. */
  crew_notes: string;
  /** When the unit went in. A `date` column, so the time half is always midnight. */
  installed: null | string;
  /** The hour meter's current reading. `*int32` with `gt=0` — null when unread. */
  running_hours: null | number;
}

export type EquipmentsListDto = List<SingleEquipmentDto>;

export interface EquipmentField {
  itemId: string;
  value: unknown;
}

export interface SingleEquipment {
  createdAt: string;
  data: {
    [name: string]: string;
  };
  id: string;
  name: string;
  type: SingleEquipmentType;
  typeId: string;
  updated_at: string;
  vesselId: string;
  fields: EquipmentField[];
  isAiGenerated: boolean;
  documents: EquipmentDocument[];
  crewNotes: string;
  installed: null | string;
  runningHours: null | number;
}

export type EquipmentsList = List<SingleEquipment>;

export enum EquipmentTypeFieldType {
  BOOLEAN = 'bool',
  INTEGER = 'integer',
  FLOAT = 'float',
  STRING = 'string',
  LONG_STRING = 'long_string',
  LIST_INTEGER = 'list_integer',
  LIST_FLOAT = 'list_float',
  LIST_STRING = 'list_string',
}
export interface EquipmentTypeFieldDto {
  created_at: string;
  equipment_type_id: string;
  field_type: EquipmentTypeFieldType;
  id: string;
  is_required: boolean;
  name: string;
  options: null;
  sort_order: number;
  updated_at: string;
  validation_rules: unknown;
}

export interface EquipmentTypeField {
  createdAt: string;
  equipmentTypeId: string;
  fieldType: EquipmentTypeFieldType;
  id: string;
  isRequired: boolean;
  name: string;
  options: null | { label: string; value: unknown }[];
  sortOrder: number;
  updatedAt: string;
  validationRules: unknown;
}

/**
 * PUT /equipment/{id} rewrites every column it names rather than patching, so a
 * field left out of this body is a field cleared on the item — everything the
 * item carries has to travel on every save.
 */
export interface CreateUpdateEquipmentDto {
  type_id: string;
  name: string;
  structure_values: EquipmentFieldDto[];
  installed: null | string;
  running_hours: null | number;
}

/** `running_hours` is an `int32` on vessel — past that the write fails outright. */
export const EQUIPMENT_RUNNING_HOURS_MAX = 2147483647;

/**
 * Vessel validates the reading as `gt=0`, so zero isn't a low reading — it's the
 * same as no reading, and null is how the meter gets cleared.
 */
export const equipmentRunningHoursSchema = z
  .number('Please enter the running hours as a number.')
  .int('Please enter the running hours as a whole number.')
  .positive('Please enter running hours above 0.')
  .max(
    EQUIPMENT_RUNNING_HOURS_MAX,
    `Please enter ${EQUIPMENT_RUNNING_HOURS_MAX.toLocaleString('en-US')} running hours or fewer.`,
  )
  .nullable();

/** A `date` column, held here as the ISO string the date picker round-trips. */
export const equipmentInstalledSchema = z.string().nullable();

/** Vessel caps the blob at 3000 chars (`dto.SetEquipmentCrewNotesRequest`). */
export const EQUIPMENT_CREW_NOTES_MAX_LENGTH = 3000;

export interface SetEquipmentCrewNotesDto {
  crew_notes: string;
}

/** No minimum: emptying the field is how the notes get cleared. */
export const equipmentCrewNotesSchema = z
  .string()
  .max(
    EQUIPMENT_CREW_NOTES_MAX_LENGTH,
    `Please keep the crew notes to ${EQUIPMENT_CREW_NOTES_MAX_LENGTH} characters or fewer.`,
  );

const equipmentFieldsValidatorsMap: Record<
  EquipmentTypeFieldType,
  (name: string, required: boolean) => ZodTypeAny
> = {
  [EquipmentTypeFieldType.STRING]: (name, required) => {
    const rule = z.string();
    if (required) {
      return rule.min(1, `Please enter the ${name}.`);
    }
    return rule.nullable().optional();
  },
  [EquipmentTypeFieldType.BOOLEAN]: () => z.boolean(),
  [EquipmentTypeFieldType.FLOAT]: (name, required) => {
    const rule = z.number(`Please enter the ${name} as a number.`);
    if (required) {
      return rule;
    }
    return rule.nullable().optional();
  },
  [EquipmentTypeFieldType.INTEGER]: (name, required) => {
    const rule = z
      .number(`Please enter the ${name} as a number.`)
      .int(`Please enter the ${name} as a whole number.`);
    if (required) {
      return rule;
    }
    return rule.nullable().optional();
  },
  [EquipmentTypeFieldType.LIST_FLOAT]: (name, required) => {
    const rule = z.number(`Please enter the ${name} as a number.`);
    if (required) {
      return rule;
    }
    return rule.nullable().optional();
  },
  [EquipmentTypeFieldType.LIST_INTEGER]: (name, required) => {
    const rule = z
      .number(`Please enter the ${name} as a number.`)
      .int(`Please enter the ${name} as a whole number.`);
    if (required) {
      return rule;
    }
    return rule.nullable().optional();
  },
  [EquipmentTypeFieldType.LIST_STRING]: (name, required) => {
    const rule = z.string();
    if (required) {
      return rule.min(1, `Please enter the ${name}.`);
    }
    return rule.nullable().optional();
  },
  [EquipmentTypeFieldType.LONG_STRING]: (name, required) => {
    const rule = z.string();
    if (required) {
      return rule.min(1, `Please enter the ${name}.`);
    }
    return rule.nullable().optional();
  },
};

export const getCreateUpdateEquipmentSchema = (fields?: EquipmentTypeField[]) => {
  const dynamicFields = z.object(
    fields?.reduce((acc, { id, fieldType, name }) => {
      return {
        ...acc,
        [id]: equipmentFieldsValidatorsMap[fieldType](name, false),
      };
    }, {}),
  );
  return z.object({
    typeId: z.string().min(1, 'Please choose a type.'),
    name: z.string().min(1, 'Please enter a name.'),
    installed: equipmentInstalledSchema,
    runningHours: equipmentRunningHoursSchema,
    fields: dynamicFields,
  });
};

export type CreateUpdateEquipmentFormFields = z.infer<
  ReturnType<typeof getCreateUpdateEquipmentSchema>
>;

/**
 * Editing an existing item also edits its crew notes. They travel with the form
 * but not with the item's own PUT — vessel keeps them behind their own endpoint,
 * so the save splits back into two calls.
 */
export const getUpdateEquipmentSchema = (fields?: EquipmentTypeField[]) =>
  getCreateUpdateEquipmentSchema(fields).extend({ crewNotes: equipmentCrewNotesSchema });

export type UpdateEquipmentFormFields = z.infer<ReturnType<typeof getUpdateEquipmentSchema>>;
